import type { Message } from "@/components/types";
import { cleanTextForTTS } from "@/lib/tts/clean-text";

export interface PracticeRecommendation {
  topic?: string | null;
  difficulty?: string | null;
  types?: string[] | null;
}

export interface ReviewPlanTerm {
  term: string;
  definition?: string;
  example?: string;
  note?: string;
}

const MAX_VOCAB_EXAMPLE_LENGTH = 200;
const MAX_TOPIC_LENGTH = 100;
const MAX_SHADOW_CONTEXT = 200;

export type ToastI18nAdapter = (
  key: string,
  fallback: string,
  values?: Record<string, any>,
) => string;

/**
 * 清理词汇例句：去掉 emoji、markdown 符号、拼音括号，只保留一句最干净的中文示例。
 */
export function cleanReviewExample(example: string): string {
  if (!example.trim()) return "";
  const stripped = cleanTextForTTS(example)
    // 去掉 ( ... ) 与 （ ... ） 里的拼音/英文翻译
    .replace(/\([^)]*\)/g, "")
    .replace(/（[^）]*）/g, "")
    .replace(/^\s*[-–—]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  // 优先取第一句完整中文句子（以中文标点结尾）
  const firstSentence = stripped.match(
    /[\u4e00-\u9fa5][^。！？.；…]*[。！？.；…]/,
  );
  if (firstSentence) return firstSentence[0].trim();
  // 没有中文标点时，返回前 120 个字符兜底
  return stripped.slice(0, 120).trim();
}

export function trimExample(example: string): string {
  return cleanReviewExample(example).slice(0, MAX_VOCAB_EXAMPLE_LENGTH);
}

/**
 * 从回复正文中提取包含该词的句子作为例句——兜底用。
 * 早期实现把整条回复塞进 example（含提示词泄漏的 "** Core Answer**"
 * 之类段落标题），这里改为按句切分后取第一条含词句，无则返回空串。
 */
export function extractSentenceWithWord(text: string, word: string): string {
  if (!text || !word) return "";
  const sentences = text.split(/(?<=[。！？!?；;…])|\n+/);
  const hit = sentences.find((s) => s.includes(word));
  return hit ?? "";
}

export function createVocabularyRequestBody(word: string, example: string) {
  const sanitizedExample = trimExample(example);
  return {
    word,
    meaning: word,
    example: sanitizedExample,
    notes: "chat",
    tags: "chat",
    category: "chat",
    difficulty: 1,
  };
}

export function buildPracticeSessionPayload(
  context: string,
  recommendation?: PracticeRecommendation | null,
) {
  const trimmedContext = context.trim();
  const recommendedTopic = recommendation?.topic?.trim();
  const topicSource =
    recommendedTopic && recommendedTopic.length
      ? recommendedTopic
      : trimmedContext;
  const topic = topicSource.slice(0, MAX_TOPIC_LENGTH);

  const rawDifficulty =
    typeof recommendation?.difficulty === "string"
      ? recommendation.difficulty
      : undefined;
  const hasMeaningfulDifficulty = Boolean(rawDifficulty?.trim().length);
  const hasTypes =
    Array.isArray(recommendation?.types) && recommendation.types.length > 0;
  const sanitizedTypes =
    hasTypes && recommendation?.types
      ? recommendation.types
          .map((type) => type.trim())
          .filter((type) => type.length > 0)
      : undefined;

  return {
    topic,
    difficulty: hasMeaningfulDifficulty ? rawDifficulty : undefined,
    types:
      sanitizedTypes && sanitizedTypes.length > 0 ? sanitizedTypes : undefined,
    shadowText: trimmedContext.slice(0, MAX_SHADOW_CONTEXT),
  };
}

export function formatPracticeToastDescription(
  sessionId: string | null,
  translate: ToastI18nAdapter,
) {
  const idText = sessionId ?? "N/A";
  return translate(
    "chat.practice.session_ready",
    `Practice session ${idText} is ready.`,
    {
      id: idText,
    },
  );
}

export function buildReviewPlanTerms(
  message: Message | null,
  fallbackContext: string,
): ReviewPlanTerm[] {
  if (!message) return [];
  const baseContext = fallbackContext.trim();

  if (Array.isArray(message.vocabDetails) && message.vocabDetails.length > 0) {
    const normalizedDetails = message.vocabDetails
      .filter((item) => Boolean(item?.term && item.term.trim().length))
      .map((item) => ({
        term: item.term,
        definition: item.definition ?? undefined,
        example: item.example ? trimExample(item.example) : undefined,
        note: item.note ?? undefined,
      }));
    if (normalizedDetails.length > 0) {
      return normalizedDetails;
    }
  }

  if (Array.isArray(message.vocabList) && message.vocabList.length > 0) {
    return message.vocabList
      .filter((term) => typeof term === "string" && term.trim().length > 0)
      .map((term) => ({
        term,
        example: undefined,
      }));
  }

  return [];
}

export function formatReviewPlanToastDescription(
  locale: string,
  sessionId: string | null,
  translate: ToastI18nAdapter,
) {
  if (sessionId) {
    return translate(
      "chat.practice.review_created_with_id",
      `Review plan created (ID ${sessionId}).`,
      { id: sessionId },
    );
  }
  return translate(
    "chat.practice.review_created",
    "Review plan created. Visit the review tab to continue.",
  );
}
